import { ArrowRightLeft, Clock, DollarSign, History, RotateCcw, Send } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	ButtonGroup,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupDetailText } from 'features/common/components/Punchup/composites/content/PunchupDetailText';
import { PunchupFeed, PunchupFeedItem } from 'features/common/components/Punchup/primitives/display/PunchupFeed';
import {
	useTicketHistory,
	useTicketDetails,
	TicketHistoryEntry,
} from 'features/PunchupPages/CustomFieldsResponse/hooks/useEventCustomFields';
import { formatTransactionType } from './transactionLabels';
import styles from './modals.module.css';

const transactionIcon = (type: string) => {
	if (type === 'transferred') return <Send size={16} />;
	if (type === 'refunded') return <RotateCcw size={16} />;
	if (type === 'primary_sold') return <DollarSign size={16} />;
	if (type === 'switched_to' || type === 'switched_from') return <ArrowRightLeft size={16} />;
	return <Clock size={16} />;
};

interface HistoryModalProps {
	ticketId: number;
	onClose: () => void;
}

export const HistoryModal: FC<HistoryModalProps> = ({ ticketId, onClose }) => {
	const { t } = useTranslation();
	const { data: historyData, isLoading: historyLoading, error: historyError } = useTicketHistory(ticketId);
	const { data: ticketDetails, isLoading: ticketLoading } = useTicketDetails(ticketId);

	return (
		<PunchupDialogue maxWidth={600} onClose={onClose}>
			<DialogIcon>
				<History size={24} />
			</DialogIcon>
			<DialogTitle>{t('attendee-actions-history', 'Ticket History')}</DialogTitle>
			<PunchupDetailText
				main={t('attendee-actions-history-main', 'A full record of all transactions for this ticket.')}
				secondary={t(
					'attendee-actions-history-secondary',
					'Each entry shows what happened, when, and which user triggered it. Transaction types: Sale (original purchase), Transferred (ownership changed), Switched (moved to a different show or ticket type), Canceled, and Refunded. Ticket Details shows the current scan status.'
				)}
				collapsible
			/>
			{historyLoading || ticketLoading ? (
				<div className={styles.fetchSpinner}>
					<PunchupSpinner size={32} />
				</div>
			) : historyError ? (
				<p className={styles.bodyText}>{t('error-loading-history', 'Error loading history')}</p>
			) : !historyData || !Array.isArray(historyData) || historyData.length === 0 ? (
				<p className={styles.bodyText}>{t('attendee-actions-history-empty', 'No history found')}</p>
			) : (
				<>
					<div className={styles.ticketInfo}>
						<p className={styles.ticketInfoTitle}>{t('ticket-information', 'Ticket Details')}</p>
						<p className={styles.ticketInfoRow}>
							<strong>{t('scanned-status', 'Scan Status')}:</strong>{' '}
							{ticketDetails?.validated ? t('scanned', 'Scanned') : t('not-scanned', 'Not Scanned')}
						</p>
						{ticketDetails?.validated && (
							<p className={styles.ticketInfoRow}>
								<strong>{t('times-scanned', 'Times Scanned')}:</strong> {ticketDetails.validated_times ?? 0}
							</p>
						)}
						{historyData[0]?.UsedPromotion && (
							<p className={styles.ticketInfoRow}>
								<strong>{t('promotion-applied', 'Promotion Applied')}:</strong> {String(historyData[0].UsedPromotion)}
							</p>
						)}
					</div>
					<div className={styles.feedScroll}>
						<PunchupFeed>
							{historyData.map((item: TicketHistoryEntry) => {
								const tx = item.Transaction;
								const user = item.TransactionUser;
								const label = formatTransactionType(tx.transaction_type);
								return (
									<PunchupFeedItem
										key={tx.ID}
										icon={transactionIcon(tx.transaction_type)}
										label={label}
										timestamp={new Date(tx.CreatedAt).toLocaleString()}
									>
										<span>
											{t('user', 'User')}: <strong>{user.full_name}</strong> ({user.email})
										</span>
										{(tx.promo_amount_applied ?? 0) > 0 && (
											<span>
												{t('promotion-applied', 'Promotion applied')}: ${tx.promo_amount_applied}
											</span>
										)}
									</PunchupFeedItem>
								);
							})}
						</PunchupFeed>
					</div>
				</>
			)}
			<ButtonGroup>
				<PunchupButton variant="secondary" text={t('close', 'Close')} onClick={onClose} />
			</ButtonGroup>
		</PunchupDialogue>
	);
};
